import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

export default function CustomDrawer({
  isOpen = false,
  onClose,

  direction = "right",

  width = 420,
  height = 420,

  children,

  overlayColor = "rgba(0,0,0,0.35)",

  drawerClassName = "",
  backdropClassName = "",

  closeOnBackdropClick = true,
  closeOnEsc = true,

  // IMPORTANT
  transitionDuration = 550,

  // smoother premium easing
  transitionEasing = "cubic-bezier(0.16, 1, 0.3, 1)",

  zIndex = 99999,
}) {
  const [mounted, setMounted] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // open/close lifecycle
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);

      // allow browser paint first
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true);
        });
      });

      document.body.style.overflow = "hidden";

      return () => cancelAnimationFrame(raf);
    }

    // CLOSE
    setVisible(false);

    document.body.style.overflow = "auto";

    const timeout = setTimeout(() => {
      setShouldRender(false);
    }, transitionDuration);

    return () => clearTimeout(timeout);
  }, [isOpen, transitionDuration]);

  // ESC close
  useEffect(() => {
    if (!closeOnEsc) return;

    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen, closeOnEsc, onClose]);

  const handleBackdropClick = () => {
    if (closeOnBackdropClick) {
      onClose?.();
    }
  };

  // smoother than exact 100%
  const hiddenTransforms = {
    right: "translate3d(105%,0,0)",
    left: "translate3d(-105%,0,0)",
    top: "translate3d(0,-105%,0)",
    bottom: "translate3d(0,105%,0)",
  };

  const visibleTransform = "translate3d(0,0,0)";

  const drawerStyles = {
    position: "fixed",
    zIndex: zIndex + 1,

    transform: visible ? visibleTransform : hiddenTransforms[direction],

    opacity: visible ? 1 : 0.98,

    transition: `
      transform ${transitionDuration}ms ${transitionEasing},
      opacity ${transitionDuration}ms ease
    `,

    willChange: "transform, opacity",

    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    transformStyle: "preserve-3d",

    boxShadow: "0 20px 70px rgba(0,0,0,0.18)",

    overflow: "auto",
  };

  // positioning
  switch (direction) {
    case "left":
      Object.assign(drawerStyles, {
        top: 0,
        left: 0,
        width: typeof width === "number" ? `${width}px` : width,
        height: "100vh",
      });
      break;

    case "top":
      Object.assign(drawerStyles, {
        top: 0,
        left: 0,
        width: "100%",
        height: typeof height === "number" ? `${height}px` : height,
      });
      break;

    case "bottom":
      Object.assign(drawerStyles, {
        bottom: 0,
        left: 0,
        width: "100%",
        height: typeof height === "number" ? `${height}px` : height,
      });
      break;

    case "right":
    default:
      Object.assign(drawerStyles, {
        top: 0,
        right: 0,
        width: typeof width === "number" ? `${width}px` : width,
        height: "100vh",
      });
      break;
  }

  const backdropStyles = {
    position: "fixed",
    inset: 0,

    background: overlayColor,

    opacity: visible ? 1 : 0,

    transition: `
      opacity ${transitionDuration}ms ${transitionEasing},
      backdrop-filter ${transitionDuration}ms ${transitionEasing}
    `,

    backdropFilter: visible ? "blur(0px)" : "blur(0px)",

    pointerEvents: visible ? "auto" : "none",

    zIndex,

    willChange: "opacity",
  };

  if (!mounted || !shouldRender) return null;

  return createPortal(
    <>
      {/* backdrop */}
      <div
        style={backdropStyles}
        className={backdropClassName}
        onClick={handleBackdropClick}
      />

      {/* drawer */}
      <div
        role="dialog"
        aria-modal="true"
        style={drawerStyles}
        className={`bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-l border-gray-200 dark:border-gray-800 ${drawerClassName}`}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
